// cleanup_convert_to_typescript_file_LOGIN_WORKFLOW
// cleanup_remove_external_libraries_LOGIN_WORKFLOW

import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';
import { camelCase, template } from 'lodash';

import Alert from '@glu/alerts-react';
import { Input } from '@glu/form-components';
import { useValidation } from '@glu/validation-react';
import { Button, PRIMARY, TERTIARY } from '@glu/buttons-react';

import useLocale from 'components/useLocale/useLocale';
import usePlaceResolver, {
  variants as placeResolverVariants
} from 'components/usePlaceResolver/usePlaceResolver';
import Criteria from 'components/validation/Criteria';
import { setSnackBar } from 'logic/store/snackbar/actions';
import { exclude, gt, lt, match, regexp } from 'utils/validations';
import {
  regexp as gluRegExp,
  match as gluMatch,
  exclude as gluExclude
} from 'utils/validations/gluValidations';

import { changePassword, getPwdComplexityRequirements$ } from 'api/securityQuestions/service';
import { clearSession } from 'api/user/service';
import { ALERTVARIATIONS } from 'constants.js';

const { ERROR, SUCCESS } = ALERTVARIATIONS;

/*
 * @TODO CB-20319
 * Terms and Conditions
 */

const ResetPasswordComponent = ({ actions, onCancel, onSuccess, user }) => {
  const locale = useLocale();
  const dispatch = useDispatch();

  const [errorMessage, setErrorMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [passwordFieldValidations, setPasswordFieldValidations] = useState({
    matchesRegExp: false,
    minLength: false,
    maxLength: false,
    exclude: false
  });
  const [confirmPasswordMatches, setConfirmPasswordMatches] = useState(false);

  const [FormProvider, formContext, { data: formData, validate: validateForm }] = useValidation();

  const services = useMemo(() => [getPwdComplexityRequirements$()], []);
  const { Placeholder, results } = usePlaceResolver({
    services,
    variant: placeResolverVariants.spinner
  });

  const { data: pwdComplexityData, success } = results?.[0] || {};

  const dispatchSnackBarMessage = ({ message, type, uid }) =>
    dispatch(setSnackBar({ uid, message, type }));

  useEffect(() => {
    if (!Placeholder && !success) {
      setErrorMessage(locale['logon.forgotten.system.error']);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Placeholder, success]);

  const {
    maxUserPasswordLength,
    minPasswordLength,
    passwordPattern = '.*',
    passwordPatternDescription,
    passwordExclusions = []
  } = pwdComplexityData || {};

  const exclusions = passwordExclusions
    .map((key) => pwdComplexityData[camelCase(key)])
    .filter(Boolean);

  const pwdPatternRegExp = new RegExp(passwordPattern);

  const validatePassword = (value) => {
    if (!value) {
      setPasswordFieldValidations({
        matchesRegExp: false,
        minLength: false,
        maxLength: false,
        exclude: false
      });
    } else {
      setPasswordFieldValidations({
        matchesRegExp:
          regexp(value, {
            regexp: pwdPatternRegExp,
            locale
          }) === null,
        minLength: gt(value, { minLength: minPasswordLength }) === null,
        maxLength: lt(maxUserPasswordLength)(value, { maxLength: maxUserPasswordLength }) === null,
        exclude: exclude(value, { exclude: exclusions }) === null
      });
    }
  };

  const validateConfirmPassword = (value) => {
    setConfirmPasswordMatches(match(value, { match: formData?.password }) === null);
  };

  useEffect(() => {
    validatePassword(formData?.password);
    validateConfirmPassword(formData?.confirmPassword);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData?.password, formData?.confirmPassword]);

  if (Placeholder) {
    return <Placeholder />;
  }

  const clearError = () => {
    setErrorMessage('');
  };

  const errorHandler = (error) => {
    const { code, userMessage } = error?.data?.[0] || {};
    const messages = locale[code] || userMessage;
    setSending(false);
    setErrorMessage(messages || locale['logon.forgotten.system.error']);
  };

  const responseHandler = (data) => {
    const { success: passwordChangeSuccess } = data;
    if (passwordChangeSuccess) {
      setErrorMessage('');
      setSending(false);
      dispatchSnackBarMessage({
        uid: new Date().getTime(),
        message: locale['password.passwordChanged'],
        type: SUCCESS
      });
      onSuccess(data);
    } else {
      errorHandler(data);
    }
  };

  const resetPassword = (event) => {
    event.preventDefault();
    const { oldPassword, password: newPassword } = formData;
    const { customerCode, password: propsPassword, userCode } = user || {};

    validateForm().then(({ isValid }) => {
      if (isValid) {
        setSending(true);
        changePassword({
          userCode,
          customerCode,
          newPassword,
          oldPassword: propsPassword || oldPassword,
          redirectToken: sessionStorage.getItem('redirecttoken')
        })
          .then(responseHandler)
          .catch(errorHandler);
      }
    });
  };

  const getExclusionsText = () =>
    passwordExclusions
      .map((item) => locale[`password.${camelCase(item)}`])
      /**
       * @description When the locale maps to '' remove it from the list
       * "Lead Bank Code" removed when mapped to ''. Is subject to change.
       * */
      .filter(Boolean)
      /** @description Add or to the last item */
      .map((item, index, array) => (index && index === array.length - 1 ? `or ${item}` : item))
      .join(', ') || 'any personal information';

  const initialPassing = Object.values(passwordFieldValidations).every(Boolean);
  const allPassing = initialPassing && confirmPasswordMatches;

  const { mustChangePassword, password: propsPassword } = user;

  if (mustChangePassword && !propsPassword) {
    clearSession();
    return <Alert variant={ERROR} dismissible={false} message={locale['password.missingToken']} />;
  }

  return (
    <section>
      <FormProvider value={formContext}>
        <form onSubmit={resetPassword} className="col-xs-12 col-md-8 no-gutters">
          {errorMessage && (
            <Alert
              dataQa="password-error"
              variant={ERROR}
              onHide={clearError}
              message={errorMessage}
            />
          )}

          {!propsPassword && (
            <div className="form-group">
              <Input
                labelText={locale['prompt.oldPassword']}
                aria-required="true"
                disabled={sending}
                id="old-password"
                name="oldPassword"
                type="password"
                data-qa="old-password"
                validators={[{ required: true, msg: locale['validation.required'] }]}
              />
            </div>
          )}

          <div className="form-group">
            <Input
              aria-required="true"
              disabled={sending}
              labelText={locale['prompt.newPassword']}
              name="password"
              type="password"
              data-qa="new-password"
              maxLength={maxUserPasswordLength}
              maxLengthIndicatorLimit={maxUserPasswordLength}
              validators={[
                {
                  required: true,
                  msg: locale['validation.required']
                },
                {
                  minLength: minPasswordLength,
                  msg: template(locale['validation.minNCharacters'])({ min: minPasswordLength })
                },
                gluRegExp({ regexp: pwdPatternRegExp, msg: passwordPatternDescription }),
                gluExclude({
                  exclude: exclusions,
                  msg: template(locale['password.minimum.personal'])({
                    exclusions: getExclusionsText()
                  })
                })
              ]}
            />
          </div>

          <div className="form-group">
            <Input
              labelText={locale['prompt.confirmPassword']}
              aria-required="true"
              id="forgot-password-repeat"
              name="confirmPassword"
              data-qa="confirmPassword"
              type="password"
              disabled={!initialPassing || sending}
              maxLength={maxUserPasswordLength}
              maxLengthIndicatorLimit={maxUserPasswordLength}
              validators={[
                {
                  required: true,
                  msg: locale['validation.required']
                },
                gluMatch({
                  match: formData?.password,
                  msg: locale['password.match'],
                  bothDefined: true
                })
              ]}
            />
          </div>

          <div className="form-group">
            <ul className="list-unstyled">
              <li>
                <Criteria
                  passing={passwordFieldValidations?.matchesRegExp}
                  dataQa="password-pattern-criteria"
                >
                  {passwordPatternDescription}
                </Criteria>
              </li>
              <li>
                <Criteria
                  passing={passwordFieldValidations?.exclude}
                  dataQa="password-exclusion-criteria"
                >
                  {template(locale['password.minimum.personal'])({
                    exclusions: getExclusionsText()
                  })}
                </Criteria>
              </li>
              <li>
                <Criteria
                  passing={
                    passwordFieldValidations?.minLength && passwordFieldValidations?.maxLength
                  }
                  dataQa="password-length-criteria"
                >
                  {template(locale['password.minimum.characters'])({
                    minLength: minPasswordLength,
                    maxLength: maxUserPasswordLength
                  })}
                </Criteria>
              </li>
              <li>
                <Criteria passing={allPassing} dataQa="password-match-criteria">
                  {locale['password.match']}
                </Criteria>
              </li>
            </ul>
          </div>

          <div className="form-group">
            {!actions && (
              <Button
                disabled={sending}
                className="btn btn-primary"
                text={locale['common.change']}
                type="submit"
                variant={PRIMARY}
              />
            )}
            {onCancel && (
              <Button
                disabled={sending}
                className="btn btn-link"
                text={locale['common.cancel']}
                onClick={onCancel}
                variant={TERTIARY}
              />
            )}
            {actions && actions({ allPassing, sending })}
          </div>
        </form>
      </FormProvider>
    </section>
  );
};

ResetPasswordComponent.propTypes = {
  actions: PropTypes.func,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  user: PropTypes.shape({
    userCode: PropTypes.string,
    userGroup: PropTypes.string
  })
};

ResetPasswordComponent.defaultProps = {
  actions: null,
  onCancel: null,
  onSuccess() {},
  user: {}
};

export default ResetPasswordComponent;
